import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface BreakerRow {
  readonly circuitId: string;
  readonly circuitCode: string;
  readonly targetType: string;
  readonly targetRef: string;
  readonly state: string;
  readonly failureCount: number;
  readonly consecutiveFailures: number;
  readonly tripCount: number;
}

const STATES = ['', 'CLOSED', 'OPEN', 'HALF_OPEN'];
const TARGETS = ['', 'CONNECTOR', 'FLOW', 'API', 'ENDPOINT'];

/**
 * GR L29.06 — Circuit Breakers (`/admin/esb/circuit-breakers`). Live view of the
 * GR net-new `esb_circuit_breaker_state` machines guarding each integration
 * target: their state (closed / open / half-open), failure counters and how many
 * times they have tripped. Filterable by state and target type.
 */
export function GrEsbCircuitBreakersPage(): ReactNode {
  const { t: tr } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<BreakerRow[]>([]);
  const [state, setState] = useState('');
  const [targetType, setTargetType] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (state) params.set('state', state);
    if (targetType) params.set('targetType', targetType);
    const qs = params.toString();
    apiRequest<{ data: BreakerRow[] }>(`/v1/eip/circuit-breakers${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, state, targetType]);

  return (
    <main aria-label="esb-circuit-breakers">
      <h1>{tr('gr29.breakers.title')}</h1>
      <div className="filters">
        <label>
          {tr('gr29.breakers.labelState')}
          <select aria-label="filter-state" value={state} onChange={(e) => setState(e.target.value)}>
            {STATES.map((s) => <option key={s || 'all'} value={s}>{s || tr('gr29.breakers.all')}</option>)}
          </select>
        </label>
        <label>
          {tr('gr29.breakers.labelTarget')}
          <select aria-label="filter-target" value={targetType} onChange={(e) => setTargetType(e.target.value)}>
            {TARGETS.map((t) => <option key={t || 'all'} value={t}>{t || tr('gr29.breakers.all')}</option>)}
          </select>
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{tr('gr29.breakers.loading')}</p> : (
        <>
          <p aria-label="breaker-count">{tr('gr29.breakers.count', { count: String(rows.length) })}</p>
          {rows.length === 0 ? <p aria-label="empty">{tr('gr29.breakers.empty')}</p> : (
            <table aria-label="breaker-table">
              <thead><tr><th>{tr('gr29.breakers.colCode')}</th><th>{tr('gr29.breakers.colTarget')}</th><th>{tr('gr29.breakers.colRef')}</th><th>{tr('gr29.breakers.colState')}</th><th>{tr('gr29.breakers.colFailures')}</th><th>{tr('gr29.breakers.colTrips')}</th></tr></thead>
              <tbody>
                {rows.map((b) => (
                  <tr key={b.circuitId}>
                    <td>{b.circuitCode}</td><td>{b.targetType}</td><td>{b.targetRef}</td>
                    <td aria-label={`state-${b.circuitId}`}>{b.state}</td>
                    <td>{b.failureCount}</td><td>{b.tripCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </main>
  );
}
